import { useId, useState, type FormEvent } from "react";
import { FORM_FIELDS, FORM_KIND_FR, FORM_KINDS, normalizeVaultAdd, type FieldKind, type FormKind, type VaultAddInput } from "../../shared/vault";
import { Button, Field, Input, Notice, Segmented, Textarea } from "./ui";

/**
 * The vault's « Ajouter » form (page Coffre, and the one-tap link page /coffre/ajouter the agent sends). Values are
 * checked here with the same rules as the server (shared/vault.ts normalizeVaultAdd), sent ONCE over HTTPS by
 * `onSubmit`, then the inputs are emptied: nothing is kept in the page, nothing in the browser's storage.
 */
export function VaultAddForm({
  kinds = FORM_KINDS,
  initialKind = "login",
  initialSite = null,
  initialLabel = null,
  siteLocked = false,
  submitLabel = "Enregistrer dans le coffre",
  onSubmit,
  onDone,
}: {
  kinds?: readonly FormKind[];
  initialKind?: FormKind;
  initialSite?: string | null;
  initialLabel?: string | null;
  /** The one-tap link names its site: shown, not editable (the server binds the item to it anyway). */
  siteLocked?: boolean;
  submitLabel?: string;
  onSubmit: (input: VaultAddInput) => Promise<{ n: number; replaced?: boolean }>;
  onDone?: (n: number, replaced: boolean) => void;
}) {
  const id = useId();
  const [kind, setKind] = useState<FormKind>(initialKind);
  const [label, setLabel] = useState(initialLabel ?? "");
  const [website, setWebsite] = useState(initialSite ?? "");
  const [values, setValues] = useState<Partial<Record<FieldKind, string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(null);
    const input: VaultAddInput = { kind, label: label.trim() || null, website: kind === "login" ? website.trim() || null : null, fields: values };
    const checked = normalizeVaultAdd(input);
    if (!checked.ok) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    try {
      const r = await onSubmit(input);
      // The values leave the page at once.
      setValues({});
      setLabel("");
      if (!initialSite) setWebsite("");
      setDone(r.replaced ? `Élément n°${r.n} mis à jour, chiffré.` : `Gardé dans le coffre (n°${r.n}), chiffré.`);
      onDone?.(r.n, r.replaced === true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'élément n'a pas pu être enregistré.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={(e) => void submit(e)} data-testid="vault-add-form" autoComplete="off">
      {kinds.length > 1 ? (
        <Segmented
          name={`${id}-kind`}
          legend="Type d'élément"
          value={kind}
          options={kinds.map((k) => ({ value: k, label: FORM_KIND_FR[k] }))}
          onChange={(k) => {
            setKind(k);
            setValues({});
            setError(null);
          }}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {kind === "login" ? (
          <Field label="Site" htmlFor={`${id}-site`} hint="L'adresse de la page de connexion, par exemple https://www.netflix.com">
            <Input id={`${id}-site`} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" inputMode="url" autoComplete="off" required readOnly={siteLocked && !!initialSite} />
          </Field>
        ) : null}
        <Field label="Titre (facultatif)" htmlFor={`${id}-label`} hint="Ce que ton agent voit de cet élément (jamais les valeurs)">
          <Input id={`${id}-label`} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} autoComplete="off" />
        </Field>
        {FORM_FIELDS[kind].map((spec) => {
          const fieldId = `${id}-${spec.kind}`;
          const value = values[spec.kind] ?? "";
          const set = (v: string) => setValues((prev) => ({ ...prev, [spec.kind]: v }));
          return (
            <Field key={spec.kind} label={`${spec.label}${spec.required ? "" : ""}`} htmlFor={fieldId} className={spec.multiline ? "sm:col-span-2" : undefined}>
              {spec.multiline ? (
                <Textarea id={fieldId} value={value} onChange={(e) => set(e.target.value)} autoComplete="off" spellCheck={false} required={spec.required} />
              ) : (
                <Input
                  id={fieldId}
                  type={spec.secret ? "password" : "text"}
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  placeholder={spec.placeholder}
                  autoComplete={spec.autocomplete ?? "off"}
                  inputMode={spec.inputMode}
                  spellCheck={false}
                  required={spec.required}
                  data-1p-ignore
                  data-lpignore="true"
                />
              )}
            </Field>
          );
        })}
      </div>
      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      {done ? (
        <Notice tone="success" role="status">
          {done}
        </Notice>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy}>
          {busy ? "Chiffrement…" : submitLabel}
        </Button>
        <p className="text-[12.5px] text-subtle">Envoyé une seule fois, chiffré aussitôt. Ni ton agent ni personne ne le voit en clair.</p>
      </div>
    </form>
  );
}
